import { useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { toast } from "sonner";
import { EnvelopeSimple, CalendarCheck } from "@phosphor-icons/react";
import { fmtDate } from "../../lib/dateUtil";

/** Weekly HSE digest: 8am on the day each admin picks (default Monday) — what it would say now, your own day/switch, the company master switch. */
export default function HseDigestCard() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [nudging, setNudging] = useState(false);
  useEffect(() => { API.get("/hse/digest").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return null;

  const toggleAll = async (enabled) => {
    try { await API.put("/hse/digest", { enabled }); setD(x => ({ ...x, enabled })); toast.success(enabled ? "Digest on for everyone" : "Digest off for everyone"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveMine = async (patch) => {
    try { const { data } = await API.put("/hse/digest/me", patch); setD(x => ({ ...x, mine: { ...x.mine, ...data } })); toast.success(data.enabled ? `Yours arrives ${data.day_label}s at 8am` : "Your copy is switched off"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const sendNow = async () => {
    setBusy(true);
    try { const { data } = await API.post("/hse/digest/send-now"); toast.success(data.sent ? `Sent to ${data.to}` : data.detail || "Notification sent"); } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const nudge = async () => {
    setNudging(true);
    try {
      const { data } = await API.post("/hse/digest/nudge-operatives");
      toast.success(`Nudged ${data.nudged} operative${data.nudged === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} already nudged today` : ""}`);
      const fresh = await API.get("/hse/digest"); setD(fresh.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setNudging(false); }
  };
  const c = d.counts;
  const people = d.ack_people || [];
  const tone = c.attention ? "border-l-red-500" : "border-l-emerald-500";
  const mineOn = d.enabled && d.mine.enabled;

  return (
    <div className={`bg-card border border-border rounded-lg p-4 border-l-4 ${tone} space-y-3`} data-testid="hse-digest-card">
      <div className="flex flex-wrap items-center gap-3">
        <CalendarCheck size={22} className={c.attention ? "text-red-600" : "text-emerald-600"} />
        <div className="flex-1 min-w-[240px]">
          <p className="text-sm font-semibold">Weekly HSE digest <span className="text-xs font-normal text-muted-foreground">· 8am · in-app + email</span></p>
          <p className="text-xs text-muted-foreground" data-testid="hse-digest-summary">{d.summary}</p>
        </div>
        <Button size="sm" variant="outline" onClick={sendNow} disabled={busy} data-testid="hse-digest-send-now"><EnvelopeSimple size={14} className="mr-1" /> {busy ? "Sending…" : "Send me it now"}</Button>
      </div>
      {people.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs border-t border-border pt-3" data-testid="hse-digest-nudges">
          <span className="font-medium">{people.length} operative{people.length === 1 ? " still owes" : "s still owe"} read receipts</span>
          <span className="text-muted-foreground truncate max-w-[520px]">
            · {people.slice(0, 3).map(p => `${p.name} (${p.docs.length}${p.nudge_due ? `, ${p.longest_wait}d` : ""})`).join(", ")}{people.length > 3 ? ` +${people.length - 3} more` : ""}
          </span>
          <span className="flex-1" />
          <span className="text-muted-foreground">{c.nudges_due ? `${c.nudges_due} waiting 7+ days — nudged every Monday${c.nudges_due === c.nudged_today ? " · all nudged today" : ""}` : "Nobody has waited 7 days yet"}</span>
          <Button size="sm" variant="outline" className="h-7" onClick={nudge} disabled={nudging || !c.nudges_due || c.nudges_due === c.nudged_today} data-testid="hse-digest-nudge-now">{nudging ? "Nudging…" : c.nudges_due && c.nudges_due === c.nudged_today ? "Nudged today" : "Nudge them now"}</Button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs border-t border-border pt-3">
        <label className="flex items-center gap-2" data-testid="hse-digest-mine">
          <Switch checked={!!d.mine.enabled} disabled={!d.enabled} onCheckedChange={(v) => saveMine({ enabled: v })} data-testid="hse-digest-my-toggle" />
          <span>My copy{d.mine.enabled ? "" : " (off)"}</span>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-muted-foreground">Day</span>
          <select value={d.mine.day} disabled={!mineOn} onChange={e => saveMine({ day: Number(e.target.value) })} className="h-7 rounded-md border border-input bg-background px-2 text-xs disabled:opacity-50" data-testid="hse-digest-my-day">
            {d.days.map((name, i) => <option key={name} value={i}>{name}</option>)}
          </select>
        </label>
        {d.mine.last_sent_at && <span className="text-muted-foreground" data-testid="hse-digest-last-sent">Last sent {fmtDate(d.mine.last_sent_at)}</span>}
        <span className="flex-1" />
        <label className="flex items-center gap-2" title="Master switch for every admin on the company"><Switch checked={!!d.enabled} onCheckedChange={toggleAll} data-testid="hse-digest-toggle" /> <span>Everyone{d.enabled ? "" : " (off)"}</span></label>
      </div>
    </div>
  );
}
